import axios from "axios";
import { URL } from "../../../config/urls";
import { Config } from "../../../config/regions";

const DEFAULT_API_URL = process.env.NODE_ENV === "development" 
  ? `${Config.BASE_URL}/api/${URL.AI_CHAT}` //to use local ai models or custom modals on local dev
  : `${Config.BASE_URL}/api/${URL.AI_CHAT}`;
const DEFAULT_MODEL = "nvidia/nemotron-3-ultra-550b-a55b:free";
const FALLBACK_MODELS = [
  "nvidia/nemotron-3-ultra-550b-a55b:free", 
  "nvidia/nemotron-3-super-120b-a12b:free", 
  "google/gemma-4-31b-it:free"
];
const API_KEY = process.env.NODE_ENV === "development" 
  ? process.env.REACT_APP_OPENROUTER_API_KEY 
  : null;

// Default configuration
const DEFAULT_CONFIG = {
  timeout: 60000, // 60 seconds
  maxRetries: 3,
  retryDelay: 1000, // Base delay for exponential backoff (ms)
  maxRetryDelay: 10000, // Max delay between retries (ms)
  retryStatusCodes: [408, 429, 500, 502, 503, 504], // Status codes to retry
};

/**
 * Custom error classes for better error handling
 */
export class AIServiceError extends Error {
  constructor(message, code, statusCode, originalError, context = {}) {
    super(message);
    this.name = "AIServiceError";
    this.code = code;
    this.statusCode = statusCode;
    this.originalError = originalError;
    this.context = context;
    this.timestamp = new Date().toISOString();
  }
}

export class AIServiceNetworkError extends AIServiceError {
  constructor(message, originalError, context = {}) {
    super(message, "NETWORK_ERROR", 0, originalError, context);
    this.name = "AIServiceNetworkError";
  }
}

export class AIServiceTimeoutError extends AIServiceError {
  constructor(message, originalError, context = {}) {
    super(message, "TIMEOUT_ERROR", 408, originalError, context);
    this.name = "AIServiceTimeoutError";
  }
}

export class AIServiceRateLimitError extends AIServiceError {
  constructor(message, originalError, context = {}, retryAfter = null) {
    super(message, "RATE_LIMIT_ERROR", 429, originalError, { ...context, retryAfter });
    this.name = "AIServiceRateLimitError";
    this.retryAfter = retryAfter;
  }
}

export class AIServiceAuthError extends AIServiceError {
  constructor(message, originalError, context = {}) {
    super(message, "AUTH_ERROR", 401, originalError, context);
    this.name = "AIServiceAuthError";
  }
}

export class AIServiceValidationError extends AIServiceError {
  constructor(message, originalError, context = {}) {
    super(message, "VALIDATION_ERROR", 400, originalError, context);
    this.name = "AIServiceValidationError";
  }
}

export class AIServiceServerError extends AIServiceError {
  constructor(message, originalError, context = {}) {
    super(message, "SERVER_ERROR", 500, originalError, context);
    this.name = "AIServiceServerError";
  }
}

export class AIServiceModelError extends AIServiceError {
  constructor(message, originalError, context = {}) {
    super(message, "MODEL_ERROR", 500, originalError, context);
    this.name = "AIServiceModelError";
  }
}

/**
 * Sleep utility for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay with jitter
 */
const calculateRetryDelay = (attempt, baseDelay, maxDelay) => {
  const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  // Add jitter (±25%) to prevent thundering herd
  const jitter = delay * 0.25 * (Math.random() * 2 - 1);
  return Math.floor(delay + jitter);
};

/**
 * Check if error is retryable
 */
const isRetryableError = (error, retryStatusCodes) => {
  if (!error.response) return true; // Network errors are retryable
  return retryStatusCodes.includes(error.response.status);
};

/**
 * Validate chat completion request parameters
 */
const validateChatCompletionParams = ({ messages, maxTokens, temperature }) => {
  if (!messages || !Array.isArray(messages) || messages.length === 0) {
    throw new AIServiceValidationError(
      "Messages array is required and must not be empty",
      null,
      { messages }
    );
  }

  if (maxTokens !== undefined && (maxTokens < 1 || maxTokens > 32000)) {
    throw new AIServiceValidationError(
      "maxTokens must be between 1 and 32000",
      null,
      { maxTokens }
    );
  }

  if (temperature !== undefined && (temperature < 0 || temperature > 2)) {
    throw new AIServiceValidationError(
      "temperature must be between 0 and 2",
      null,
      { temperature }
    );
  }

  // Validate message structure
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (!msg.role || !["system", "user", "assistant", "tool"].includes(msg.role)) {
      throw new AIServiceValidationError(
        `Invalid role at message index ${i}: ${msg.role}`,
        null,
        { messageIndex: i, role: msg.role }
      );
    }
    if (msg.content === undefined && !msg.tool_calls) {
      throw new AIServiceValidationError(
        `Message at index ${i} must have content or tool_calls`,
        null,
        { messageIndex: i }
      );
    }
  }
};

export default class AIService {
  constructor({ 
    apiUrl = DEFAULT_API_URL, 
    model = DEFAULT_MODEL, 
    fallbackmodels = FALLBACK_MODELS, 
    apiKey = API_KEY,
    orgId,
    config = {}
  } = {}) {
    this.apiUrl = apiUrl;
    this.model = model;
    this.fallbackmodels = fallbackmodels;
    this.apiKey = apiKey;
    this.config = { ...DEFAULT_CONFIG, ...config };
    
    // Create axios instance with default config
    this.client = axios.create({
      baseURL: this.apiUrl,
      timeout: this.config.timeout,
      params: orgId ? { orgId } : {},
      headers: {
        "Content-Type": "application/json",
        ...(this.apiKey ? { "Authorization": `Bearer ${this.apiKey}` } : {}),
      },
    });

    // Add request/response interceptors for logging
    this._setupInterceptors();
  }

  /**
   * Setup axios interceptors for logging and error handling
   */
  _setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add request timestamp for latency tracking
        config.metadata = { startTime: Date.now() };
        if (process.env.NODE_ENV === "development") {
          console.debug("[AIService] Request:", {
            url: config.url,
            method: config.method,
            model: this.model,
            messageCount: config.data?.messages?.length,
          });
        }
        return config;
      },
      (error) => {
        console.error("[AIService] Request error:", error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        const latency = Date.now() - response.config.metadata?.startTime;
        if (process.env.NODE_ENV === "development") {
          console.debug("[AIService] Response:", {
            status: response.status,
            latency: `${latency}ms`,
            model: response.data?.model,
            usage: response.data?.usage,
          });
        }
        return response;
      },
      (error) => {
        const latency = error.config?.metadata?.startTime 
          ? Date.now() - error.config.metadata.startTime 
          : null;
        console.error("[AIService] Response error:", {
          status: error.response?.status,
          latency: latency ? `${latency}ms` : "unknown",
          message: error.message,
          code: error.code,
        });
        return Promise.reject(error);
      }
    );
  }

  /**
   * Create chat completion with retry logic and fallback models
   */
  async createChatCompletion({
    messages,
    tools,
    toolChoice = "auto",
    maxTokens = 1000,
    temperature = 0.2,
    signal, // AbortSignal for cancellation
  }) {
    // Validate input parameters
    validateChatCompletionParams({ messages, maxTokens, temperature });

    const requestPayload = {
      model: this.model,
      models: this.fallbackmodels,
      max_tokens: maxTokens,
      temperature,
      messages,
      tools,
      tool_choice: toolChoice,
    };

    let lastError = null;
    const { maxRetries, retryDelay, maxRetryDelay, retryStatusCodes } = this.config;

    // Try with primary model first, then fallback models
    const modelsToTry = [this.model, ...this.fallbackmodels.filter(m => m !== this.model)];

    for (let modelIndex = 0; modelIndex < modelsToTry.length; modelIndex++) {
      const currentModel = modelsToTry[modelIndex];
      const isFallback = modelIndex > 0;
      
      if (isFallback) {
        console.warn(`[AIService] Falling back to model: ${currentModel}`);
      }

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          // Check for abort signal
          if (signal?.aborted) {
            throw new AIServiceError(
              "Request aborted",
              "ABORTED",
              0,
              null,
              { model: currentModel, attempt }
            );
          }

          const response = await this.client.post(
            "", // Relative to baseURL
            { ...requestPayload, model: currentModel },
            { signal }
          );

          const { data } = response;

          // Check for API-level errors in successful responses
          if (data.error) {
            throw new AIServiceError(
              data.message || data.error?.message || "Assistant request failed",
              "API_ERROR",
              response.status,
              null,
              { model: currentModel, responseData: data }
            );
          }

          // Add metadata to response
          return {
            ...data,
            _metadata: {
              model: currentModel,
              isFallback,
              attempt: attempt + 1,
              latency: Date.now() - response.config.metadata.startTime,
            },
          };
        } catch (error) {
          lastError = this._normalizeError(error, { model: currentModel, attempt, isFallback });

          // Don't retry on certain errors
          if (this._isNonRetryableError(lastError)) {
            throw lastError;
          }

          // Don't retry on last attempt
          if (attempt === maxRetries) {
            break;
          }

          // Check if we should retry
          if (!isRetryableError(error, retryStatusCodes)) {
            break;
          }

          // Calculate delay and wait
          const delay = calculateRetryDelay(attempt, retryDelay, maxRetryDelay);
          console.warn(
            `[AIService] Attempt ${attempt + 1} failed for model ${currentModel}, ` +
            `retrying in ${delay}ms: ${lastError.message}`
          );
          await sleep(delay);
        }
      }

      // If we exhausted retries for this model, try next fallback model
      if (modelIndex < modelsToTry.length - 1) {
        console.warn(
          `[AIService] All retries exhausted for model ${currentModel}, ` +
          `trying fallback model: ${modelsToTry[modelIndex + 1]}`
        );
      }
    }

    // All models and retries exhausted
    throw lastError || new AIServiceError(
      "All models and retries exhausted",
      "ALL_RETRIES_EXHAUSTED",
      500,
      null,
      { modelsTried: modelsToTry }
    );
  }

  /**
   * Normalize various error types into AIServiceError subclasses
   */
  _normalizeError(error, context = {}) {
    // Already an AIServiceError
    if (error instanceof AIServiceError) {
      return error;
    }

    // AbortError from AbortController
    if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
      return new AIServiceError(
        "Request was cancelled",
        "ABORTED",
        0,
        error,
        context
      );
    }

    // Axios error with response
    if (error.response) {
      const { status, data } = error.response;
      const message = data?.error?.message || data?.message || error.message;
      const errorCode = data?.error?.code || data?.code;

      switch (status) {
        case 400:
          return new AIServiceValidationError(message, error, { ...context, errorCode });
        case 401:
        case 403:
          return new AIServiceAuthError(message, error, { ...context, errorCode });
        case 408:
          return new AIServiceTimeoutError(message, error, { ...context, errorCode });
        case 429: {
          const retryAfter = error.response.headers?.["retry-after"] 
            ? parseInt(error.response.headers["retry-after"], 10) * 1000 
            : null;
          return new AIServiceRateLimitError(message, error, { ...context, errorCode }, retryAfter);
        }
        case 500:
        case 502:
        case 503:
        case 504:
          return new AIServiceServerError(message, error, { ...context, errorCode });
        default:
          return new AIServiceError(message, "API_ERROR", status, error, { ...context, errorCode });
      }
    }

    // Network/timeout errors (no response)
    if (error.request) {
      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        return new AIServiceTimeoutError(
          "Request timeout",
          error,
          context
        );
      }
      return new AIServiceNetworkError(
        "Unable to reach the assistant service",
        error,
        context
      );
    }

    // Request setup errors
    return new AIServiceError(
      error.message || "Assistant request failed",
      "REQUEST_ERROR",
      0,
      error,
      context
    );
  }

  /**
   * Check if error should not be retried
   */
  _isNonRetryableError(error) {
    return (
      error instanceof AIServiceValidationError ||
      error instanceof AIServiceAuthError ||
      error.code === "ABORTED"
    );
  }

  /**
   * Update configuration at runtime
   */
  updateConfig(config) {
    this.config = { ...this.config, ...config };
    this.client.defaults.timeout = this.config.timeout;
  }

  /**
   * Get current configuration
   */
  getConfig() {
    return { ...this.config };
  }

  /**
   * Health check endpoint
   */
  async healthCheck() {
    try {
      const response = await this.client.get("/health", { timeout: 5000 });
      return { healthy: true, status: response.status, data: response.data };
    } catch (error) {
      return { 
        healthy: false, 
        error: this._normalizeError(error, { operation: "healthCheck" }).message 
      };
    }
  }
}
